import { apiUrl, authenticatedFetch } from './auth';

// The announcement bar at the top of the client dashboard (HHLTB-2818).

export interface CampaignMessage {
    headline: string;
    // TipTap HTML. The API reduces it to the tags the editor can produce.
    body: string;
    is_active: boolean;
    // YYYY-MM-DD, inclusive, read in HHL's timezone. Empty means no limit.
    start_date: string | null;
    end_date: string | null;
    // CampaignStatus codes the bar is shown to.
    status_codes: string[];
    updated_at: string;
    updated_by_name: string;
}

export interface CampaignStatusOption {
    code: string;
    name: string;
}

export interface CampaignMessageResponse {
    message: CampaignMessage;
    status_options: CampaignStatusOption[];
}

export type CampaignMessagePayload = Omit<
    CampaignMessage,
    'updated_at' | 'updated_by_name'
>;

// DRF returns field errors as { field: ['message'] }. Surface the first one so
// the toast says what to fix instead of a generic failure.
const parseError = async (resp: Response, fallback: string) => {
    let data: any = null;
    try {
        data = await resp.json();
    } catch {
        // non-JSON body; fall through to the generic message
    }
    const firstFieldError = data
        ? Object.values(data).flat().find(v => typeof v === 'string')
        : null;
    const error: any = new Error(data?.detail || firstFieldError || fallback);
    error.data = data;
    return error;
};

export const fetchCampaignMessage = async (): Promise<CampaignMessageResponse> => {
    const resp = await authenticatedFetch(`${apiUrl}/admin/campaign-message`, {
        method: 'get',
    });
    if (!resp.ok) throw await parseError(resp, 'Error fetching the campaign message');
    return await resp.json();
};

export const saveCampaignMessage = async (
    payload: CampaignMessagePayload,
): Promise<CampaignMessageResponse> => {
    const resp = await authenticatedFetch(`${apiUrl}/admin/campaign-message`, {
        method: 'PUT',
        body: JSON.stringify(payload),
    });
    if (!resp.ok) throw await parseError(resp, 'Error saving the campaign message');
    return await resp.json();
};
